import {
  WEBINAR_STEPS,
  WEBINAR_STEP_HINTS,
  WEBINAR_STEP_LABELS,
  type WebinarStep,
} from "@xop/dashboard-shared";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Step,
  StepButton,
  StepContent as MuiStepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { useCan } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import { formatWhen } from "./WebinarsListPage.js";
import { webinarsApi } from "./client.js";
import { StepAttendees } from "./steps/StepAttendees.js";
import { StepContent } from "./steps/StepContent.js";
import { StepInvite } from "./steps/StepInvite.js";
import { StepLeads } from "./steps/StepLeads.js";
import { StepSurvey } from "./steps/StepSurvey.js";
import type { StepProps } from "./stepShared.js";
import { BackButton } from "@/components/ui/back-button";

/**
 * Marketing ▸ Webinars — the wizard.
 *
 * ONE vertical stepper, opened on the first unfinished step, with exactly one
 * step expanded at a time. That shape is the whole point: the operator is
 * never looking at five screens' worth of controls wondering which to press,
 * and every step states in a sentence what it does before it offers a button.
 *
 * Steps can still be revisited in any order (the labels are buttons) — the
 * order is guidance, not a cage — but the DEFAULT is always "here is the next
 * thing to do".
 */

const STEP_COMPONENTS: Record<WebinarStep, (props: StepProps) => JSX.Element> = {
  invite: StepInvite,
  attendees: StepAttendees,
  content: StepContent,
  survey: StepSurvey,
  leads: StepLeads,
};

export function WebinarDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const webinarId = Number(id);
  const navigate = useNavigate();
  const canEdit = useCan("marketing:edit");
  const [active, setActive] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["webinars", "detail", webinarId],
    queryFn: () => webinarsApi.get(webinarId),
    enabled: Number.isFinite(webinarId),
  });

  // Open on the first unfinished step — once. After that the operator's own
  // navigation wins, so a refetch after "mark done" does not yank them
  // somewhere they did not ask to go.
  useEffect(() => {
    if (active !== null || !data) return;
    const next = data.webinar.next_step;
    setActive(next ? WEBINAR_STEPS.indexOf(next) : WEBINAR_STEPS.length - 1);
  }, [active, data]);

  const setStep = useMutation({
    mutationFn: ({ step, done }: { step: WebinarStep; done: boolean }) =>
      webinarsApi.setStep(webinarId, step, done),
    onSuccess: (webinar, vars) => {
      void refetch();
      if (vars.done) {
        const idx = WEBINAR_STEPS.indexOf(vars.step);
        // Move on to the next unfinished step, or stay put on the last one.
        const next = webinar.next_step
          ? WEBINAR_STEPS.indexOf(webinar.next_step)
          : Math.min(idx + 1, WEBINAR_STEPS.length - 1);
        setActive(next);
      }
    },
    onError: (e: Error) =>
      toast({ title: "Could not update the step", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: () => webinarsApi.remove(webinarId),
    onSuccess: () => {
      toast({ title: "Webinar deleted" });
      navigate("/marketing/webinars");
    },
    onError: (e: Error) =>
      toast({ title: "Could not delete", description: e.message, variant: "destructive" }),
  });

  // Error FIRST. `active` is only set once data arrives, so checking the
  // spinner condition first left a failed load spinning forever instead of
  // saying what went wrong.
  if (error || (!isLoading && !data)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Could not open this webinar. {(error as Error)?.message ?? ""}
        </Alert>
        <BackButton to="/marketing/webinars" label="Back to all webinars" sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (isLoading || active === null || !data) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { webinar } = data;
  // Every step gets the same props — the whole detail payload plus the two
  // callbacks — so a step never has to fetch anything to render.
  const stepProps: StepProps = {
    detail: data,
    refresh: () => void refetch(),
    canEdit,
    markDone: (s) => setStep.mutate({ step: s, done: true }),
  };

  return (
    <Box sx={{ p: 3, display: "grid", gap: 3, maxWidth: 1100 }}>
      <Box>
        <Button
          component={RouterLink}
          to="/marketing/webinars"
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ mb: 1 }}
        >
          All webinars
        </Button>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
              {webinar.title}
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.25 }}>
              {webinar.scheduled_at ? formatWhen(webinar.scheduled_at) : "No date set yet"}
              {" · "}
              {webinar.duration_minutes} minutes
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              size="small"
              color={webinar.status === "complete" ? "success" : "primary"}
              variant={webinar.status === "draft" ? "outlined" : "filled"}
              label={`${webinar.completed_steps.length} of ${WEBINAR_STEPS.length} steps done`}
            />
            {canEdit && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-webinar"
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Stepper activeStep={active} orientation="vertical" nonLinear>
        {WEBINAR_STEPS.map((step, index) => {
          const Component = STEP_COMPONENTS[step];
          const done = webinar.completed_steps.includes(step);
          return (
            <Step key={step} completed={done} data-testid={`step-${step}`}>
              <StepButton onClick={() => setActive(index)}>
                {/* The hint lives INSIDE the label rather than in MUI's
                    `optional` slot: `StepButton` clones its child label and
                    drops that slot, so the "what do I do here" line silently
                    disappeared — and that line is the reason a first-time
                    operator can follow this at all. */}
                <StepLabel>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    {WEBINAR_STEP_LABELS[step]}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {WEBINAR_STEP_HINTS[step]}
                  </Typography>
                </StepLabel>
              </StepButton>
              <MuiStepContent>
                <Box sx={{ pt: 1, pb: 2 }}>
                  <Component {...stepProps} />
                </Box>
              </MuiStepContent>
            </Step>
          );
        })}
      </Stepper>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this webinar?"
        description="The attendee list and everything the AI wrote for it are deleted too. The email campaigns in the CRM are not touched."
        confirmLabel="Delete"
        onConfirm={async () => {
          await remove.mutateAsync();
        }}
      />
    </Box>
  );
}
